import { azureLoginUri, commonHeader, generateEndpoint } from "~/config/apiConfig";
import { ErrorBoundary } from "~/errors/errorBoundary";
import type {
  InviteGuest,
  InviteMember,
  InviteMemberResponse,
  Organization,
  UpdateOrganization,
} from "~/interface/organization";
import type { User } from "~/interface/user";
import { type ApiResult, failure, success } from "./apiResult";

export class OrganizationApi {
  private getRequestInfo(method: string, requestBody?: object): RequestInit {
    return {
      method: method,
      headers: commonHeader,
      credentials: "include",
      keepalive: true,
      ...{
        body: JSON.stringify(requestBody),
      },
    };
  }

  private getRequestBody(baseBody: object, extraBody?: object): object {
    // ベースとなる共通データと、メソッドごとの追加データをマージ
    return { ...baseBody, ...extraBody };
  }

  async get(organization_id: string): Promise<Organization | null> {
    const endpoint = generateEndpoint(`/organizations/${organization_id}`);

    try {
      const requestInfo = this.getRequestInfo("GET");
      const response = await fetch(endpoint, requestInfo);
      if (response.ok) {
        const organization = await response.json();
        return organization;
      } else if (response.status === 422) {
        window.location.href = azureLoginUri;
        return null;
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error(errorMessage);
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async getMembers(organization_id: string): Promise<User[] | null> {
    const endpoint = generateEndpoint(`/organizations/${organization_id}/members`);
    const requestInfo = this.getRequestInfo("GET");
    const response = await fetch(endpoint, requestInfo);

    try {
      if (response.ok) {
        const members = await response.json();
        return members;
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error(errorMessage);
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async getGuests(organization_id: string): Promise<User[] | null> {
    const endpoint = generateEndpoint(`/organizations/${organization_id}/guests`);
    const requestInfo = this.getRequestInfo("GET");
    const response = await fetch(endpoint, requestInfo);

    try {
      if (response.ok) {
        const guests = await response.json();
        return guests;
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error(errorMessage);
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async update(id: string, updateData: UpdateOrganization): Promise<Organization | null> {
    const endpoint = generateEndpoint(`/organizations/${id}`);

    try {
      const requestInfo = this.getRequestInfo("POST", updateData);
      const response = await fetch(endpoint, requestInfo);

      if (response.ok) {
        const organization = await response.json();
        return organization;
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error(errorMessage);
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async convertCreditToChatRequest(organization_id: string, amount: number): Promise<boolean> {
    const convertCreditToChatRequest = generateEndpoint(
      `/organizations/${organization_id}/credits/convert`,
    );

    try {
      const requestInfo = this.getRequestInfo("POST", { amount: amount });
      const requestBody = this.getRequestBody(requestInfo);
      const response = await fetch(convertCreditToChatRequest, requestBody);
      if (response.ok) {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  async inviteMember(
    organization_id: string,
    inviteData: InviteMember,
  ): Promise<ApiResult<InviteMemberResponse>> {
    const inviteUser = generateEndpoint(`/organizations/${organization_id}/members`);

    try {
      const requestInfo = this.getRequestInfo("POST", inviteData);
      const requestBody = this.getRequestBody(requestInfo);

      const response = await fetch(inviteUser, requestBody);
      if (response.ok) {
        const result: InviteMemberResponse = await response.json();
        return success(result);
      } else {
        let errorMessage: string;
        if (response.status >= 500) {
          errorMessage = ErrorBoundary(response.status);
        } else {
          try {
            const errorData = await response.json();
            const message = errorData.message as string;
            // 空文字列チェックを追加
            errorMessage = message?.trim() ? message : ErrorBoundary(response.status);
          } catch {
            errorMessage = ErrorBoundary(response.status);
          }
        }
        console.error(errorMessage);
        return failure(errorMessage);
      }
    } catch (error) {
      console.error(error);
      const errorMessage =
        error instanceof Error ? error.message : "ネットワークエラーが発生しました";
      return failure(errorMessage);
    }
  }

  async inviteGuest(
    organization_id: string,
    inviteData: InviteGuest,
  ): Promise<ApiResult<InviteMemberResponse>> {
    const inviteGuest = generateEndpoint(`/organizations/${organization_id}/guests`);

    try {
      const requestInfo = this.getRequestInfo("POST", inviteData);
      const requestBody = this.getRequestBody(requestInfo);

      const response = await fetch(inviteGuest, requestBody);
      if (response.ok) {
        const result: InviteMemberResponse = await response.json();
        return success(result);
      } else {
        let errorMessage: string;
        if (response.status >= 500) {
          errorMessage = ErrorBoundary(response.status);
        } else {
          try {
            const errorData = await response.json();
            const message = errorData.message as string;
            // 空文字列チェックを追加
            errorMessage = message?.trim() ? message : ErrorBoundary(response.status);
          } catch {
            errorMessage = ErrorBoundary(response.status);
          }
        }
        console.error(errorMessage);
        return failure(errorMessage);
      }
    } catch (error) {
      console.error(error);
      const errorMessage =
        error instanceof Error ? error.message : "ネットワークエラーが発生しました";
      return failure(errorMessage);
    }
  }

  async sendInviteEmail(organization_id: string, target_user_id: string): Promise<ApiResult<void>> {
    const sendInviteEmail = generateEndpoint(
      `/organizations/${organization_id}/invitations/${target_user_id}`,
    );
    const requestInfo = this.getRequestInfo("POST");
    const requestBody = this.getRequestBody(requestInfo);

    try {
      const response = await fetch(sendInviteEmail, requestBody);
      if (response.ok) {
        return success(undefined);
      } else {
        let errorMessage: string;
        if (response.status >= 500) {
          errorMessage = ErrorBoundary(response.status);
        } else {
          try {
            const errorData = await response.json();
            const message = errorData.message as string;
            // 空文字列チェックを追加
            errorMessage = message?.trim() ? message : ErrorBoundary(response.status);
          } catch {
            errorMessage = ErrorBoundary(response.status);
          }
        }
        console.error(errorMessage);
        return failure(errorMessage);
      }
    } catch (error) {
      console.error(error);
      const errorMessage =
        error instanceof Error ? error.message : "ネットワークエラーが発生しました";
      return failure(errorMessage);
    }
  }
}
